/**
 * ✅ NEW NEWS MANAGEMENT SERVICE
 * Replaces buggy news caching and state management
 * 
 * Firestore Structure:
 * users/{userId}/newsConfig/
 *   - currentSources (doc) - Active news sources
 *   - cache (doc) - Latest fetched news data
 *   - seenArticles (doc) - URLs of shown articles per source
 */

import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp,
  collection,
  writeBatch
} from 'firebase/firestore';

class NewsManagementService {
  constructor(db, userId) {
    this.db = db;
    this.userId = userId;
    this.CACHE_DURATION_MS = 60 * 60 * 1000; // 1 hour
  }

  /**
   * ✅ Save user's selected news sources
   */
  async saveSelectedSources(selectedSources) {
    if (!this.userId) {
      throw new Error('User not authenticated');
    }

    try {
      const sourcesRef = doc(this.db, 'users', this.userId, 'newsConfig', 'currentSources');
      
      await setDoc(sourcesRef, {
        sources: selectedSources, // Array of source IDs
        updatedAt: serverTimestamp(),
        lastModified: new Date().toISOString() // ✅ Store ISO string, not Firestore timestamp
      });

      console.log('✅ News sources saved:', selectedSources);
      return { success: true, sources: selectedSources };
    } catch (error) {
      console.error('❌ Failed to save news sources:', error);
      throw error;
    }
  }

  /**
   * ✅ Get user's selected news sources
   */
  async getSelectedSources() {
    if (!this.userId) {
      throw new Error('User not authenticated');
    }

    try {
      const sourcesRef = doc(this.db, 'users', this.userId, 'newsConfig', 'currentSources');
      const sourcesDoc = await getDoc(sourcesRef);

      if (sourcesDoc.exists()) {
        const data = sourcesDoc.data();
        return {
          sources: data.sources || [],
          lastModified: data.lastModified || null
        };
      }

      // Return defaults for new users
      return {
        sources: ['abc', 'variety', 'yahoo-sports'],
        lastModified: null
      };
    } catch (error) {
      console.error('❌ Failed to get selected sources:', error);
      return {
        sources: ['abc', 'variety', 'yahoo-sports'],
        lastModified: null
      };
    }
  }

  /**
   * ✅ Save fetched news data to cache
   */
  async saveNewsCache(newsData, sourcesList) {
    if (!this.userId) {
      throw new Error('User not authenticated');
    }

    try {
      const cacheRef = doc(this.db, 'users', this.userId, 'newsConfig', 'cache');
      
      const cacheData = {
        data: newsData, // Full news object with all sources
        sources: sourcesList, // Array of source IDs that were fetched
        fetchedAt: new Date().toISOString(), // ✅ ISO string for easy comparison
        version: '3.0' // Version tracking
      };

      await setDoc(cacheRef, cacheData);
      
      console.log('✅ News cache saved:', {
        sources: sourcesList,
        articles: Object.values(newsData).reduce((sum, src) => sum + (src.stories?.length || 0), 0)
      });

      return { success: true };
    } catch (error) {
      console.error('❌ Failed to save news cache:', error);
      throw error;
    }
  }

  /**
   * ✅ Get cached news data with validation
   */
  async getNewsCache() {
    if (!this.userId) {
      throw new Error('User not authenticated');
    }

    try {
      const cacheRef = doc(this.db, 'users', this.userId, 'newsConfig', 'cache');
      const cacheDoc = await getDoc(cacheRef);

      if (!cacheDoc.exists()) {
        console.log('📭 No news cache found');
        return null;
      }

      const cache = cacheDoc.data();
      
      // ✅ Validate cache structure
      if (!cache.data || !cache.fetchedAt || !cache.sources) {
        console.warn('⚠️ Invalid cache structure, clearing...');
        await this.clearCache();
        return null;
      }

      // ✅ Check cache age
      const fetchedAt = new Date(cache.fetchedAt);
      const now = new Date();
      const ageMs = now - fetchedAt;

      if (ageMs > this.CACHE_DURATION_MS) {
        console.log('⏰ Cache expired (age:', Math.round(ageMs / 1000 / 60), 'minutes)');
        return null;
      }

      console.log('✅ Valid cache found:', {
        sources: cache.sources,
        age: Math.round(ageMs / 1000 / 60) + ' minutes',
        articles: Object.values(cache.data).reduce((sum, src) => sum + (src.stories?.length || 0), 0)
      });

      return {
        data: cache.data,
        sources: cache.sources,
        fetchedAt: cache.fetchedAt,
        version: cache.version
      };
    } catch (error) {
      console.error('❌ Failed to get news cache:', error);
      return null;
    }
  }

  /**
   * ✅ Check if cache is valid for specific sources
   */
  async isCacheValidForSources(requestedSources) {
    const cache = await this.getNewsCache();
    
    if (!cache) return false;

    // Check if cache has ALL requested sources
    const hasAllSources = requestedSources.every(source => 
      cache.sources.includes(source) && cache.data[source]
    );

    if (!hasAllSources) {
      console.log('❌ Cache missing some sources');
      return false;
    }

    console.log('✅ Cache valid for requested sources');
    return true;
  }

  /**
   * ✅ Clear news cache
   */
  async clearCache() {
    if (!this.userId) return;

    try {
      const cacheRef = doc(this.db, 'users', this.userId, 'newsConfig', 'cache');
      await setDoc(cacheRef, {
        data: {},
        sources: [],
        fetchedAt: new Date().toISOString(),
        cleared: true
      });

      console.log('✅ News cache cleared');
    } catch (error) {
      console.error('❌ Failed to clear cache:', error);
    }
  }

  /**
   * ✅ Mark article as seen
   */
  async markArticleAsSeen(sourceId, articleUrl) {
    if (!this.userId) return;

    try {
      const seenRef = doc(this.db, 'users', this.userId, 'newsConfig', 'seenArticles');
      const seenDoc = await getDoc(seenRef);

      let seenData = {};
      if (seenDoc.exists()) {
        seenData = seenDoc.data();
      }

      // Add article URL to source's seen list
      if (!seenData[sourceId]) {
        seenData[sourceId] = [];
      }

      if (!seenData[sourceId].includes(articleUrl)) {
        seenData[sourceId].push(articleUrl);
        
        // Keep only last 50 articles per source
        if (seenData[sourceId].length > 50) {
          seenData[sourceId] = seenData[sourceId].slice(-50);
        }

        await setDoc(seenRef, {
          ...seenData,
          lastUpdated: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('❌ Failed to mark article as seen:', error);
    }
  }

  /**
   * ✅ Get seen articles for a source
   */
  async getSeenArticles(sourceId) {
    if (!this.userId) return [];

    try {
      const seenRef = doc(this.db, 'users', this.userId, 'newsConfig', 'seenArticles');
      const seenDoc = await getDoc(seenRef);

      if (seenDoc.exists()) {
        const data = seenDoc.data();
        return data[sourceId] || [];
      }

      return [];
    } catch (error) {
      console.error('❌ Failed to get seen articles:', error);
      return [];
    }
  }

  /**
   * ✅ Clear seen articles for a source
   */
  async clearSeenArticles(sourceId) {
    if (!this.userId) return;

    try {
      const seenRef = doc(this.db, 'users', this.userId, 'newsConfig', 'seenArticles');
      const seenDoc = await getDoc(seenRef);

      if (seenDoc.exists()) {
        const data = seenDoc.data();
        delete data[sourceId];

        await setDoc(seenRef, {
          ...data,
          lastUpdated: new Date().toISOString()
        });

        console.log('✅ Cleared seen articles for:', sourceId);
      }
    } catch (error) {
      console.error('❌ Failed to clear seen articles:', error);
    }
  }

  /**
   * ✅ MIGRATION HELPER: Move old data to new structure
   */
  async migrateOldNewsData(oldNewsCache, oldSelectedFeeds) {
    if (!this.userId) return;

    try {
      console.log('🔄 Migrating old news data to new structure...');

      // Save selected sources
      if (oldSelectedFeeds && oldSelectedFeeds.length > 0) {
        await this.saveSelectedSources(oldSelectedFeeds);
        console.log('✅ Migrated selected sources');
      }

      // Save cache if valid
      if (oldNewsCache && oldNewsCache.data && Object.keys(oldNewsCache.data).length > 0) {
        await this.saveNewsCache(oldNewsCache.data, oldSelectedFeeds);
        console.log('✅ Migrated news cache');
      }

      console.log('✅ Migration complete');
      return { success: true };
    } catch (error) {
      console.error('❌ Migration failed:', error);
      return { success: false, error };
    }
  }
}

export default NewsManagementService;